import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Linkedin, Activity, Users, Shield, Search, Layers } from 'lucide-react';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import AboutUsModal from './AboutUsModal';

const Footer = () => {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showLastUpdated, setShowLastUpdated] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(false);

  useEffect(() => {
    // Fetch the build-time metadata
    const fetchBuildInfo = async () => {
      try {
        const response = await fetch('/build-info.json?t=' + new Date().getTime());
        if (response.ok) {
          const buildInfo = await response.json();
          const timeString = buildInfo.lastUpdatedTime ? ` at ${buildInfo.lastUpdatedTime}` : '';
          setLastUpdated(`${buildInfo.lastUpdated}${timeString}`);
        }
      } catch (error) {
        console.log('Build info not available');
      }
    };

    fetchBuildInfo();
  }, []);
  return (
    <footer className="bg-gray-900 text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center">
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontSize: '28px',
                fontWeight: '500',
                position: 'relative',
              }}>
                {/* Circular Arrow Icon forming "O" */}
                <div style={{
                  width: '28px',
                  height: '28px',
                  position: 'relative',
                  flexShrink: 0,
                  marginRight: '4px',
                }}>
                  <svg viewBox="0 0 50 50" style={{
                    width: '100%',
                    height: '100%',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 188, 212, 0.3))',
                  }}>
                    <defs>
                      <linearGradient id="footerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#00BCD4', stopOpacity: 1 }} />
                        <stop offset="50%" style={{ stopColor: '#0891B2', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#2563EB', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    <g>
                      <path
                        fill="url(#footerLogoGradient)"
                        stroke="url(#footerLogoGradient)"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="
                        M 11.2,22.6
                        L 3.3,21.2
                        A 22 22 0 0 1 44.1,14.0
                        L 47.5,12.0
                        L 42.7,21.9
                        L 33.7,20.0
                        L 37.1,18.0
                        A 14 14 0 0 0 11.2,22.6
                        Z" />

                      <path
                        fill="url(#footerLogoGradient)"
                        stroke="url(#footerLogoGradient)"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="
                        M 38.8,27.4
                        L 46.7,28.8
                        A 22 22 0 0 1 6.0,36.0
                        L 2.5,38.0
                        L 7.3,28.1
                        L 16.3,30.0
                        L 12.9,32.0
                        A 14 14 0 0 0 38.8,27.4
                        Z" />
                    </g>
                  </svg>
                </div>

                {/* Text part "rdinaa" */}
                <span style={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  color: '#22D3EE',
                }}>rdinaa</span>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Empowering enterprises with AI-driven solutions across finance, recruitment, procurement, and quantum security. Ordinaa delivers intelligent automation, workflow optimization, and crypto-agility for the future of business operations.
            </p>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-cyan-400">Products</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/products/lendsmart" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                  <Activity size={16} /> LendSmart AI
                </Link>
              </li>
              <li>
                <Link to="/products/niyukti" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                  <Users size={16} /> Niyukti AI
                </Link>
              </li>
              <li>
                <Link to="/products/tenderlens" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                  <Search size={16} /> TenderLens AI
                </Link>
              </li>
              <li>
                <Link to="/products/deepflow" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                  <Layers size={16} /> DeepFlow AI
                </Link>
              </li>
              <li>
                <Link to="/products/qsm" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                  <Shield size={16} /> PQC Migration
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-cyan-400">Company</h3>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => setShowAboutUs(true)}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  About Us
                </button>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-cyan-400">Get in Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400">
                <Mail size={20} className="text-cyan-500 flex-shrink-0" />
                <a href="mailto:contact@ordinaa.com" className="hover:text-white transition-colors">contact@ordinaa.com</a>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Phone size={20} className="text-cyan-500 flex-shrink-0" />
                <a href="tel:+919380470079" className="hover:text-white transition-colors">+91 9380470079</a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 pt-2">
                <a href="https://www.linkedin.com/company/ordinaa" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-cyan-600 hover:text-white transition-all duration-300 hover:scale-110 hover:rotate-6">
                  <Linkedin size={20} />
                </a>
                <span className="text-gray-500">LinkedIn</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <p className="text-gray-500 text-sm flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setShowLastUpdated(!showLastUpdated)}
                className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
                title="Click to see last update"
              >
                ©
              </button>
              {new Date().getFullYear()} Ordinaa. All rights reserved.
              {showLastUpdated && lastUpdated && (
                <span className="text-gray-500 text-xs ml-1">
                  (Last updated: {lastUpdated})
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-6 text-sm text-gray-500">
            <button
              onClick={() => setShowPrivacyPolicy(true)}
              className="hover:text-white transition-colors text-left"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setShowTermsOfService(true)}
              className="hover:text-white transition-colors text-left"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>

      {/* About Us Modal */}
      <AboutUsModal isOpen={showAboutUs} onClose={() => setShowAboutUs(false)} />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal isOpen={showTermsOfService} onClose={() => setShowTermsOfService(false)} />
    </footer>
  );
};

export default Footer;
